import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LoadingIndicator from "../components/LoadingIndicator";

const ResellerPage = () => {
  const [resellers, setResellers] = useState([]);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [newReseller, setNewReseller] = useState({
    name: "",
    email: "",
    credits: 0,
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [search, setSearch] = useState("");
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [detailedReseller, setDetailedReseller] = useState(null);
  
  // New state for transaction filtering
  const [transactionFilter, setTransactionFilter] = useState({
    startDate: "",
    endDate: "",
    transactionType: "all",
  });
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"

  // New state for reset confirmation dialog
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetResellerId, setResetResellerId] = useState(null);

  // Helper function to generate a random password.
  const generateRandomPassword = (length = 10) => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // Fetch resellers once on mount.
  useEffect(() => {
    fetchResellers();
  }, []);

  const fetchResellers = async () => {
    axios
      .get("http://localhost:5000/api/reseller", { withCredentials: true })
      .then((response) => {
        console.log("✅ Resellers Data:", response.data);
        setResellers(response.data);
      })
      .catch((error) =>
        console.error("❌ Error fetching Resellers:", error)
      );
  };

  const handleAddReseller = async () => {
    // Generate a random password for the new reseller.
    const generatedPassword = generateRandomPassword(10);
    // Prepare reseller data including the generated password.
    const resellerToRegister = {
      ...newReseller,
      password: generatedPassword,
    };
  
    try {
      // Call the registration endpoint.
      await axios.post("http://localhost:5000/api/reseller", resellerToRegister, {
        withCredentials: true,
      });
      setSnackbarMessage(
        "Reseller added successfully. An activation email has been sent."
      );
      setOpenSnackbar(true);
      setOpenAddModal(false);
      fetchResellers();
    } catch (error) {
      console.error("Error adding reseller:", error);
    }
  };

  const handleEditReseller = async () => {
    try {
      await axios.put(
        `http://localhost:5000/api/reseller/update/${selectedReseller._id}`,
        selectedReseller,
        { withCredentials: true }
      );
      setSnackbarMessage("Reseller updated successfully");
      setOpenSnackbar(true);
      setOpenEditModal(false);
      fetchResellers();
    } catch (error) {
      console.error("Error updating reseller:", error);
    }
  };

  const confirmResetCredits = async () => {
    try {
      await axios.post(
        `http://localhost:5000/api/reseller/reset`,
        { resellerId: resetResellerId },
        { withCredentials: true }
      );
      setSnackbarMessage("Reseller credits reset successfully");
      setOpenSnackbar(true);
      fetchResellers();
    } catch (error) {
      console.error("Error resetting reseller credits:", error);
    } finally {
      setOpenResetDialog(false);
      setResetResellerId(null);
    }
  };

  const handleDeleteReseller = async (id) => {
    if (window.confirm("Are you sure you want to delete this reseller?")) {
      try {
        await axios.delete(`http://localhost:5000/api/reseller/${id}`, {
          withCredentials: true,
        });
        setSnackbarMessage("Reseller deleted successfully");
        setOpenSnackbar(true);
        fetchResellers();
      } catch (error) {
        console.error("Error deleting reseller:", error);
      }
    }
  };

  const handleViewDetails = async (res) => {
    try {
      const response = await axios.get(
        `http://localhost:5000/api/reseller/${res._id}`,
        { withCredentials: true }
      );
      setDetailedReseller(response.data);
      setOpenDetailsModal(true);
      // Reset any previous transaction filters when opening details
      setTransactionFilter({ startDate: "", endDate: "", transactionType: "all" });
      setSortOrder("desc");
    } catch (error) {
      console.error("Error fetching reseller details:", error);
    }
  };

  // Compute filtered resellers based on the search input.
  const filteredResellers = useMemo(() => {
    const searchTerm = search.toLowerCase();
    return resellers.filter(
      (reseller) =>
        reseller.name.toLowerCase().includes(searchTerm) ||
        reseller.email.toLowerCase().includes(searchTerm)
    );
  }, [resellers, search]);

  // Compute filtered and sorted transaction history based on filter state.
  const filteredTransactions = useMemo(() => {
    if (!detailedReseller || !detailedReseller.transactionHistory) return [];
    let txns = [...detailedReseller.transactionHistory];

    // Filter by transaction type if not "all"
    if (transactionFilter.transactionType !== "all") {
      txns = txns.filter(
        (txn) => txn.balanceType === transactionFilter.transactionType
      );
    }
    // Filter by start date
    if (transactionFilter.startDate) {
      const start = new Date(transactionFilter.startDate);
      txns = txns.filter((txn) => new Date(txn.date) >= start);
    }
    // Filter by end date
    if (transactionFilter.endDate) {
      const end = new Date(transactionFilter.endDate);
      txns = txns.filter((txn) => new Date(txn.date) <= end);
    }
    // Sort by date
    txns.sort((a, b) => {
      if (sortOrder === "asc") {
        return new Date(a.date) - new Date(b.date);
      } else {
        return new Date(b.date) - new Date(a.date);
      }
    });
    return txns;
  }, [detailedReseller, transactionFilter, sortOrder]);

  const columns = [
    { field: "name", headerName: "Name", width:250 },
    { field: "email", headerName: "Email", width:350 },
    { field: "credits", headerName: "Credits", width:150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150, // fixed width
      headerAlign: "center",
      align: "center",
      pinned: "right",
      cellClassName: "actionsCell",
      headerClassName: "actionsCell",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            color="primary"
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              mr: 1,
              "&:hover": { backgroundColor: "primary.dark" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedReseller(params.row);
              setOpenEditModal(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            sx={{
              backgroundColor: "secondary.main",
              color: "white",
              mr: 1,
              "&:hover": { backgroundColor: "secondary.dark" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Open the custom reset credits dialog.
              setResetResellerId(params.row._id);
              setOpenResetDialog(true);
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
          <IconButton
            sx={{
              backgroundColor: "secondary.main",
              color: "white",
              mr: 1,
              "&:hover": { backgroundColor: "secondary.dark" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteReseller(params.row._id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          {/* Uncomment this block if you want a separate view button.
          <IconButton
            color="primary"
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              "&:hover": { backgroundColor: "primary.dark" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton> */}
        </Box>
      ),
    },
  ];

  if (!resellers || !resellers.length) {
    return <LoadingIndicator message="Fetching Resellers data..." />;
}

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reseller Management
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ backgroundColor: "white" }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddModal(true)}
        >
          Add Reseller
        </Button>
      </Box>

      <DataGrid
        rows={filteredResellers}
        columns={columns}
        getRowId={(row) => row._id}
        autoHeight
        pageSize={5}
        
        sx={{
          backgroundColor: "white",
          width : "100%",
          // Sticky actions column styling
          "& .MuiDataGrid-cell.actionsCell": {
            position: "sticky",
            right: 0,
            backgroundColor: "white",
            zIndex: 100,
          },
          "& .MuiDataGrid-columnHeader.actionsCell": {
            position: "sticky",
            right: 0,
            backgroundColor: "white",
            zIndex: 101,
          },
        }}
        onRowClick={(params) => handleViewDetails(params.row)}
      />

      {/* Add Reseller Modal */}
      <Modal open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 1,
            p: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Add Reseller
          </Typography>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newReseller.name}
            onChange={(e) =>
              setNewReseller({ ...newReseller, name: e.target.value })
            }
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={newReseller.email}
            onChange={(e) =>
              setNewReseller({ ...newReseller, email: e.target.value })
            }
          />
          <TextField
            label="Credits"
            fullWidth
            margin="normal"
            type="number"
            value={newReseller.credits}
            onChange={(e) =>
              setNewReseller({
                ...newReseller,
                credits: parseFloat(e.target.value),
              })
            }
          />
          <Button
            variant="contained"
            sx={{ marginTop: 2 }}
            onClick={handleAddReseller}
          >
            Add
          </Button>
        </Box>
      </Modal>

      {/* Edit Reseller Modal */}
      {selectedReseller && (
        <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 1,
              p: 4,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Edit Reseller
            </Typography>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={selectedReseller.name}
              onChange={(e) =>
                setSelectedReseller({
                  ...selectedReseller,
                  name: e.target.value,
                })
              }
            />
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={selectedReseller.email}
              onChange={(e) =>
                setSelectedReseller({
                  ...selectedReseller,
                  email: e.target.value,
                })
              }
            />
            <TextField
              label="Credits"
              fullWidth
              margin="normal"
              type="number"
              value={selectedReseller.credits}
              onChange={(e) =>
                setSelectedReseller({
                  ...selectedReseller,
                  credits: parseFloat(e.target.value),
                })
              }
            />
            <Button
              variant="contained"
              sx={{ marginTop: 2 }}
              onClick={handleEditReseller}
            >
              Save
            </Button>
          </Box>
        </Modal>
      )}

      {/* Snackbar with Top-Right Anchor */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Reseller Details Modal */}
      <Modal open={openDetailsModal} onClose={() => setOpenDetailsModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            borderRadius: 1,
            p: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Reseller Details
          </Typography>
          {detailedReseller ? (
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Name:</strong> {detailedReseller.name}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Email:</strong> {detailedReseller.email}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Credits:</strong> {detailedReseller.credits}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Status:</strong> {detailedReseller.status}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Activation:</strong>{" "}
                  {detailedReseller.isActivated ? "Activated" : "Not Activated"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Failed Login Attempts:</strong>{" "}
                  {detailedReseller.failedLoginAttempts}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Is Locked:</strong>{" "}
                  {detailedReseller.isLocked ? "Yes" : "No"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Transaction History:
                </Typography>
                {/* Transaction filtering controls */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={transactionFilter.startDate}
                    onChange={(e) =>
                      setTransactionFilter({
                        ...transactionFilter,
                        startDate: e.target.value,
                      })
                    }
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    value={transactionFilter.endDate}
                    onChange={(e) =>
                      setTransactionFilter({
                        ...transactionFilter,
                        endDate: e.target.value,
                      })
                    }
                  />
                  <TextField
                    select
                    label="Type"
                    value={transactionFilter.transactionType}
                    onChange={(e) =>
                      setTransactionFilter({
                        ...transactionFilter,
                        transactionType: e.target.value,
                      })
                    }
                    SelectProps={{ native: true }}
                  >
                    <option value="all">All</option>
                    <option value="CREDIT">Credit</option>
                    <option value="CURRENCY">Currency</option>
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                    }
                  >
                    Sort by Date ({sortOrder})
                  </Button>
                </Box>
                <Box
                  sx={{
                    maxHeight: 300, // increased height to display more transactions
                    overflowY: "auto",
                    pr: 1,
                    border: "1px solid #eee",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {filteredTransactions && filteredTransactions.length > 0 ? (
                    filteredTransactions.map((txn, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: 2,
                          border: "1px solid #ddd",
                          borderRadius: 2,
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Date: {new Date(txn.date).toLocaleString()}  
                        </Typography>
                        <Typography variant="body2">
                          Type: {txn.balanceType}  |  Amount: {txn.balance}
                        </Typography>
                        {/* <Typography variant="body2">
                          Amount: {txn.balance}
                        </Typography> */}
                        <Typography variant="body2">
                          Comment: {txn.comment}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2">
                      No transactions available.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography>Loading details...</Typography>
          )}
        </Box>
      </Modal>

      {/* Reset Credits Confirmation Dialog */}
      <Dialog
        open={openResetDialog}
        onClose={() => {
          setOpenResetDialog(false);
          setResetResellerId(null);
        }}
        aria-labelledby="reset-credits-dialog-title"
        aria-describedby="reset-credits-dialog-description"
      >
        <DialogTitle id="reset-credits-dialog-title">
          Reset Credits Confirmation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-credits-dialog-description">
            Are you sure you want to reset this reseller's credits?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenResetDialog(false);
              setResetResellerId(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={confirmResetCredits} color="secondary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResellerPage;
